'use client';

import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Eye } from 'lucide-react';
import { GsImage, MoXat, cn } from '@/components/help-center/latest-power-shared';
import { DaysStarIcon } from '@/components/help-center/fairtrade/fairtrade-shared';
import {
  CategoryPriceActivityBadge,
  PriceActivityModal,
} from '@/components/help-center/fairtrade/price-activity';
import { usePriceActivitySummaries } from '@/hooks/usePriceActivitySummaries';
import {
  formatFairtradeStatRange,
  type FairtradeCategoryStat,
} from '@/lib/fairtrade-catalog';
import type { PriceActivityCategorySummary } from '@/lib/price-activity-types';

type FairtradeCategoryCardsProps = {
  stats: FairtradeCategoryStat[];
  activeType: string;
  onSelectType: (type: string) => void;
  isDark?: boolean;
  /** Tick após sync de Price Activity. */
  priceActivityTick?: number;
};

function cardBackground(hex: string, isDark: boolean): string {
  if (isDark) {
    return `linear-gradient(160deg, color-mix(in srgb, ${hex} 42%, #030023) 0%, color-mix(in srgb, ${hex} 58%, #01012e) 45%, color-mix(in srgb, ${hex} 32%, #000814) 100%)`;
  }
  return `linear-gradient(160deg, color-mix(in srgb, ${hex} 88%, white) 0%, ${hex} 45%, color-mix(in srgb, ${hex} 75%, black) 100%)`;
}

/**
 * Proporções no estilo de `/powers` (PowersCategories), mas em escala de card Fairtrade.
 * Slot fixo; cada categoria tem peso visual próprio.
 */
const CARD_CATEGORY_ICON_PX: Record<string, number> = {
  smiley: 26,
  function: 40,
  stickers: 38,
  group: 36,
  games: 44,
  hug: 38,
  epic: 34,
  jinx: 38,
};
const CARD_COLLECTION_ICON_PX = 40;

function cardIconDisplayPx(stat: FairtradeCategoryStat): number {
  if (stat.isCollection) return CARD_COLLECTION_ICON_PX;
  return CARD_CATEGORY_ICON_PX[stat.type] ?? 32;
}

function PriceBar({
  label,
  range,
  kind,
}: {
  label: string;
  range: string;
  kind: 'xats' | 'days';
}) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg bg-black/30 px-2.5 py-1 backdrop-blur-[2px]">
      <span className="font-poppins text-[11px] font-medium lowercase text-white/75">{label}:</span>
      <span className="inline-flex items-center gap-1.5 font-poppins text-sm font-bold tabular-nums text-white">
        {kind === 'xats' ? (
          <MoXat className="h-3.5 w-3.5 brightness-110" />
        ) : (
          <DaysStarIcon className="h-3.5 w-3.5" />
        )}
        {range}
      </span>
    </div>
  );
}

function CategoryCard({
  stat,
  active,
  onSelect,
  isDark,
  className,
  onHoverPauseChange,
  activitySummary,
  onOpenActivity,
}: {
  stat: FairtradeCategoryStat;
  active: boolean;
  onSelect: () => void;
  isDark: boolean;
  className?: string;
  onHoverPauseChange?: (paused: boolean) => void;
  activitySummary?: PriceActivityCategorySummary;
  onOpenActivity?: () => void;
}) {
  const hex = stat.accentHex;
  const xatsRange = formatFairtradeStatRange(stat.sumMinXats, stat.sumMaxXats);
  const daysRange = formatFairtradeStatRange(stat.sumMinDays, stat.sumMaxDays);
  const iconCode = stat.sampleIcon || stat.type;
  const iconPx = cardIconDisplayPx(stat);

  return (
    <div
      data-fairtrade-card
      onMouseEnter={() => onHoverPauseChange?.(true)}
      onMouseLeave={() => onHoverPauseChange?.(false)}
      className={cn(
        'group relative flex h-full cursor-pointer flex-col gap-2 rounded-2xl px-3 py-2.5 text-left',
        'shadow-lg transition-[box-shadow,filter] duration-200',
        'hover:brightness-110 hover:shadow-xl',
        active && 'ring-2 ring-sky-200/70',
        className
      )}
      style={{
        background: cardBackground(hex, isDark),
        boxShadow: isDark
          ? `0 12px 28px rgba(0,0,0,0.45), inset 0 1px 0 color-mix(in srgb, ${hex} 28%, transparent)`
          : `0 10px 28px color-mix(in srgb, ${hex} 35%, transparent)`,
      }}
    >
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onSelect();
        }}
        className={cn(
          'absolute inset-0 z-[1] cursor-pointer rounded-2xl',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent'
        )}
        aria-pressed={active}
        aria-label={`Filtrar por ${stat.label}`}
      />

      {/* pointer-events-none: cliques passam ao botão de filtro; só o badge de atividade fica clicável */}
      <div className="pointer-events-none relative z-[2] flex h-11 shrink-0 items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1">
          <span
            className="relative flex h-11 shrink-0 items-center justify-center"
            style={{ width: iconPx }}
          >
            <span
              className="flex items-center justify-center"
              style={{ width: iconPx, height: iconPx }}
            >
              <GsImage
                code={iconCode}
                className="flex h-full w-full items-center justify-center"
                imgClassName="h-full w-full object-contain drop-shadow-sm"
                sizePx={40}
              />
            </span>
          </span>
          <span
            className={cn(
              'truncate font-poppins text-sm font-bold lowercase tracking-wide text-white',
              (stat.type === 'epic' || stat.type === 'games') && '-ml-1'
            )}
          >
            {stat.label.toLowerCase()}
          </span>
        </div>

        {stat.isCollection ? (
          <span
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-500/85 text-white shadow-sm transition-colors group-hover:bg-violet-400"
            aria-hidden
          >
            <Eye className="h-3.5 w-3.5" />
          </span>
        ) : activitySummary && onOpenActivity ? (
          <CategoryPriceActivityBadge
            summary={activitySummary}
            categoryLabel={stat.label}
            onOpen={onOpenActivity}
            className="pointer-events-auto relative z-[3]"
          />
        ) : null}
      </div>

      <div className="pointer-events-none relative z-[2] flex flex-col gap-1.5">
        <PriceBar label="xats" range={xatsRange} kind="xats" />
        <PriceBar label="days" range={daysRange} kind="days" />
      </div>
    </div>
  );
}

function CollectionsHeader({
  id,
  title,
  description,
  isDark,
}: {
  id: string;
  title: string;
  description: string;
  isDark: boolean;
}) {
  return (
    <header className="mb-4">
      <div
        className={cn(
          'relative overflow-hidden rounded-2xl border px-4 py-3.5 sm:px-5 sm:py-4',
          isDark
            ? 'border-violet-400/25 bg-gradient-to-br from-violet-500/[0.14] via-[#030023]/85 to-[#12081f]/55'
            : 'border-violet-200/90 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50/60'
        )}
      >
        <div
          className={cn(
            'pointer-events-none absolute inset-y-0 left-0 w-1 rounded-l-2xl',
            isDark
              ? 'bg-gradient-to-b from-violet-300 via-fuchsia-400 to-violet-700'
              : 'bg-gradient-to-b from-violet-400 via-fuchsia-400 to-violet-600'
          )}
          aria-hidden
        />

        <div
          className={cn(
            'pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full blur-2xl',
            isDark ? 'bg-violet-400/20' : 'bg-violet-300/40'
          )}
          aria-hidden
        />

        <div className="relative flex min-w-0 items-start gap-2">
          <GsImage
            code="supersummer"
            className="mt-0.5 flex h-14 w-14 shrink-0 items-center justify-center sm:h-16 sm:w-16"
            imgClassName="max-h-14 max-w-14 object-contain drop-shadow-sm sm:max-h-16 sm:max-w-16"
            sizePx={80}
          />

          <div className="min-w-0">
            <p
              className={cn(
                'font-poppins text-[10px] font-semibold uppercase tracking-[0.16em]',
                isDark ? 'text-violet-300/90' : 'text-violet-700/80'
              )}
            >
              Coleções temáticas
            </p>

            <h2
              id={id}
              className={cn(
                'mt-0.5 font-poppins text-lg font-bold tracking-tight sm:text-xl',
                isDark ? '!text-violet-50' : '!text-slate-900'
              )}
            >
              {title}
            </h2>

            <p
              className={cn(
                'mt-1 max-w-2xl font-inter text-xs leading-relaxed sm:text-[13px]',
                isDark ? 'text-violet-100/65' : 'text-slate-600'
              )}
            >
              {description}
            </p>
          </div>
        </div>
      </div>
    </header>
  );
}

const DRAG_THRESHOLD_PX = 8;

function InfiniteCollectionMarquee({
  items,
  activeType,
  onToggle,
  isDark,
}: {
  items: FairtradeCategoryStat[];
  activeType: string;
  onToggle: (type: string) => void;
  isDark: boolean;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const dragLayerRef = useRef<HTMLDivElement>(null);
  const hoverPausedRef = useRef(false);
  const draggingRef = useRef(false);
  const pointerIdRef = useRef<number | null>(null);
  const dragOriginXRef = useRef(0);
  const dragOriginOffsetRef = useRef(0);
  const dragOffsetRef = useRef(0);
  const movedRef = useRef(false);
  const suppressClickRef = useRef(false);
  const [paused, setPaused] = useState(false);
  const [dragging, setDragging] = useState(false);

  const durationSec = Math.max(22, items.length * 3.2);

  const applyDragOffset = (value: number) => {
    dragOffsetRef.current = value;
    const layer = dragLayerRef.current;
    if (layer) {
      layer.style.transform = `translate3d(${value}px, 0, 0)`;
    }
  };

  const syncPaused = () => {
    setPaused(hoverPausedRef.current || draggingRef.current);
  };

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const onClickCapture = (event: MouseEvent) => {
      if (!suppressClickRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClickRef.current = false;
    };

    viewport.addEventListener('click', onClickCapture, true);
    return () => viewport.removeEventListener('click', onClickCapture, true);
  }, []);

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;
    pointerIdRef.current = null;
    draggingRef.current = false;
    setDragging(false);
    syncPaused();

    try {
      viewportRef.current?.releasePointerCapture(event.pointerId);
    } catch {
      /* already released */
    }

    if (movedRef.current) {
      suppressClickRef.current = true;
    }
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 && event.pointerType === 'mouse') return;

    // Só inicia tracking — captura do pointer só após limiar de drag,
    // para o clique nos cards aplicar o filtro sem ser engolido.
    pointerIdRef.current = event.pointerId;
    dragOriginXRef.current = event.clientX;
    dragOriginOffsetRef.current = dragOffsetRef.current;
    movedRef.current = false;
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;

    const dx = event.clientX - dragOriginXRef.current;
    if (!draggingRef.current) {
      if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
      draggingRef.current = true;
      movedRef.current = true;
      setDragging(true);
      syncPaused();
      try {
        viewportRef.current?.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
    }

    applyDragOffset(dragOriginOffsetRef.current + dx);
  };

  const setCardHoverPaused = (next: boolean) => {
    hoverPausedRef.current = next;
    syncPaused();
  };

  const renderTrack = (suffix: string, inert: boolean) => (
    <div className="flex shrink-0 gap-3 pr-3" aria-hidden={inert || undefined}>
      {items.map((stat) => (
        <CategoryCard
          key={`${suffix}-${stat.type}`}
          stat={stat}
          active={activeType === stat.type}
          onSelect={() => onToggle(stat.type)}
          isDark={isDark}
          className="w-[16.75rem] shrink-0"
          onHoverPauseChange={setCardHoverPaused}
        />
      ))}
    </div>
  );

  return (
    <div
      ref={viewportRef}
      role="region"
      aria-label="Coleções Fairtrade"
      className={cn(
        'relative cursor-grab overflow-hidden touch-pan-y select-none active:cursor-grabbing',
        dragging && 'cursor-grabbing'
      )}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <div
        ref={dragLayerRef}
        className="will-change-transform"
        style={{ transform: 'translate3d(0, 0, 0)' }}
      >
        <div
          className="fairtrade-collections-marquee"
          style={{
            animationDuration: `${durationSec}s`,
            animationPlayState: paused ? 'paused' : 'running',
          }}
        >
          {renderTrack('a', false)}
          {renderTrack('b', true)}
        </div>
      </div>
    </div>
  );
}

export default function FairtradeCategoryCards({
  stats,
  activeType,
  onSelectType,
  isDark = false,
  priceActivityTick = 0,
}: FairtradeCategoryCardsProps) {
  const categories = useMemo(
    () => stats.filter((s) => !s.isCollection && s.type !== 'collection'),
    [stats]
  );
  const collections = useMemo(
    () => stats.filter((s) => s.isCollection),
    [stats]
  );
  const categoryIds = useMemo(() => categories.map((c) => c.type), [categories]);
  const { getSummary } = usePriceActivitySummaries(categoryIds, priceActivityTick);

  const [activityCategory, setActivityCategory] = useState<string | null>(null);
  const activityStat = categories.find((c) => c.type === activityCategory) ?? null;

  const toggle = (type: string) => {
    onSelectType(activeType === type ? '' : type);
  };

  if (!categories.length && !collections.length) return null;

  return (
    <div className="space-y-8">
      {categories.length > 0 ? (
        <section aria-label="Categorias Fairtrade">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-4">
            {categories.map((stat) => (
              <CategoryCard
                key={stat.type}
                stat={stat}
                active={activeType === stat.type}
                onSelect={() => toggle(stat.type)}
                isDark={isDark}
                className="min-w-0 w-full"
                activitySummary={getSummary(stat.type)}
                onOpenActivity={() => setActivityCategory(stat.type)}
              />
            ))}
          </div>
        </section>
      ) : null}

      {collections.length > 0 ? (
        <section aria-labelledby="fairtrade-collections-heading">
          <CollectionsHeader
            id="fairtrade-collections-heading"
            title="Coleções"
            description="Uma seleção de powers que ganham vida juntos. Descubra séries de powers que compartilham um mesmo tema, estilo ou universo, das coleções clássicas às mais especiais.  "
            isDark={isDark}
          />
          <InfiniteCollectionMarquee
            items={collections}
            activeType={activeType}
            onToggle={toggle}
            isDark={isDark}
          />
        </section>
      ) : null}

      {activityStat ? (
        <PriceActivityModal
          open={Boolean(activityCategory)}
          onOpenChange={(open) => {
            if (!open) setActivityCategory(null);
          }}
          categoryId={activityStat.type}
          categoryLabel={activityStat.label}
          sampleIcon={activityStat.sampleIcon || activityStat.type}
          onViewAllPowers={() => {
            onSelectType(activityStat.type);
            requestAnimationFrame(() => {
              document
                .getElementById('fairtrade-powers-panel')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
          }}
        />
      ) : null}
    </div>
  );
}
